import React from 'react'
import PropTypes from 'prop-types'

const COLON = ':'
const JS_SCHEME = `javascript${COLON}`
const VB_SCHEME = `vbscript${COLON}`

export const SUPPORTED_LANGS = ['ja', 'en']
export const DEFAULT_LANG = 'ja'

export const LANG_LABELS = {
  ja: '日本語',
  en: 'English',
}

const GUIDES = {
  ja: {
    summary: '使い方ガイド (HTML埋め込みのルール・許可リスト)',
    overview:
      '任意の HTML/CSS をサンドボックス化された <iframe> 内で表示します。' +
      '親ページから隔離され、許可リスト (allow-list) に従って検証・サニタイズされます。',
    sections: [
      {
        title: '🛡️ Guardrails (3 層防御)',
        body:
          '入力 HTML/CSS は以下の 3 層で検証・隔離されます。1 層を突破しても他の層が阻止する設計 ' +
          '(defense in depth) なので、誤って危険なコードを貼り付けても本番には到達しません。' +
          'パイプライン: Editor → 保存時 validator (npm + Rails) → DB → 公開サイトで <iframe srcdoc> ' +
          '+ CSP + bridge スクリプトを注入してレンダリング。',
        items: [
          {
            label: '① Validation 層',
            code:
              'allow-list 違反 (タグ/属性/URL スキーム/CSS パターン/サイズ/trillTrack・trillNavigate 文法) を ' +
              'error (保存を拒否) または warning (静かに strip) として処理。' +
              'npm (ダイアログプレビュー) と Rails (保存時) で同じ allow-list が動作 — どちら経由でも同じ挙動',
          },
          {
            label: '② Sandbox 層',
            code:
              'iframe sandbox 属性は安全なトークンのみ受理。allow-scripts は必須・解除不可。' +
              'allow-same-origin / allow-top-navigation / allow-popups-to-escape-sandbox / ' +
              'allow-pointer-lock / allow-presentation は自動 strip',
          },
          {
            label: '③ Content-Security-Policy (CSP) 層',
            code:
              "srcdoc の <head> に自動注入 (widget から override 不可): default-src 'none'; " +
              "img-src https: data:; style-src 'unsafe-inline'; script-src 'unsafe-inline'; " +
              "font-src https: data:; connect-src 'none'",
          },
          {
            label: '✅ 動作する',
            code:
              'inline <style>、inline style="..."、inline <script> (bridge のみ)、' +
              'img https: + data: URL、Web フォント https: + data: URL',
          },
          {
            label: '❌ ブロックされる',
            code:
              '外部 JS / CSS / iframe、widget からの fetch/XHR/WebSocket (connect-src none)、' +
              '<form action="..."> による外部送信',
          },
          {
            label: 'Error / Warning の違い',
            code:
              'Error → 保存を拒否 (禁止タグ、URL スキーム拒否、CSS パターン拒否、サイズ範囲外、不正な trillTrack/trillNavigate)。' +
              'Warning → 静かに strip、保存は成功 (allow-list 外の属性など)',
          },
          {
            label: 'デフォルト値まとめ',
            code:
              'sandbox: allow-scripts。サイズ: 50 ≤ width ≤ 1200, 50 ≤ height ≤ 2000 (0 = auto)。' +
              'postMessage リサイズ上限: 4000px。プレビュー debounce: 300ms。' +
              'trillNavigate: http(s) のみ、loopback ホスト拒否、1 ナビゲーション/ページロード',
          },
        ],
        note:
          'ダイアログプレビューに見える内容 = 保存される内容 = 本番で配信される内容 (常に一致)。' +
          '禁止項目は保存前にエラー表示されるので、危険なコードが本番に到達することはありません。' +
          '制約を超える機能が必要な場合は、validator のバイパスではなく allow-list の拡張として' +
          'プラットフォームチームへ相談してください。',
      },
      {
        title: '✅ 使えるタグ',
        items: [
          { label: 'レイアウト', code: 'div, span, section, article, header, footer, main, nav, aside' },
          { label: '段落・見出し', code: 'p, h1〜h6, blockquote, pre, code' },
          { label: '文字装飾', code: 'em, strong, b, i, u, small, mark, sub, sup' },
          { label: 'リスト', code: 'ul, ol, li, dl, dt, dd' },
          { label: '画像・メディア', code: 'img, picture, source, figure, figcaption' },
          { label: 'リンク・ボタン', code: 'a, button (onclick は trillTrack(...) のみ)' },
          { label: 'スタイル', code: '<style> ブロック / インライン style="..."' },
          { label: '表組み', code: 'table, thead, tbody, tfoot, tr, th, td' },
          { label: 'SVG', code: 'svg, path, g, rect, circle, line, polyline, polygon, text' },
          { label: '改行・区切り', code: 'br, hr' },
        ],
      },
      {
        title: '🚫 使えないタグ (自動削除)',
        body: 'script, iframe, object, embed, form, input, textarea, select, meta, base, link, frame, frameset, applet',
      },
      {
        title: '🔗 URL スキーム',
        items: [
          { label: 'href', code: 'http, https, mailto, tel' },
          { label: 'src', code: 'http, https, data:image/...' },
          {
            label: '禁止',
            code: `${JS_SCHEME}, ${VB_SCHEME}, data:text/html (空白・タブで難読化されたものも検出)`,
          },
        ],
      },
      {
        title: '⚡ GA4 イベントハンドラー (trillTrack / trillNavigate)',
        body:
          'on* 属性 (onclick など) は <a> / <button> 上で ' +
          'trillTrack(action, params) または trillNavigate(url, params?) の単一呼び出しのみ許可。' +
          'iframe からの postMessage は親ページの GA4 (gtag) + GTM dataLayer に転送されます。' +
          '複数文 (`trillTrack(...); other()`) や別関数呼び出しは reject。',
        items: [
          {
            label: 'action (必須・string)',
            code: 'GA4 イベント名。現在トラッキング基盤がサポート: タップ, 完読, page_view',
          },
          {
            label: 'params (必須・非空 object)',
            code: 'gtag に verbatim 転送される GA4 イベントパラメーター。空 ({}) や未指定だと計測は破棄されます',
          },
          {
            label: '└ event_category (推奨)',
            code:
              'グループ化用ラベル。trill-rails の例: 元記事を読む / 続きを読む / 自社プロモーション_banner_click など',
          },
          { label: '└ event_label', code: 'ターゲットを識別する人間可読の文字列' },
          { label: '└ link_url', code: 'クリック対象のリンク URL (タップ系イベントで推奨)' },
          { label: '└ value', code: '数値 (GA4 規約)' },
          { label: '└ non_interaction', code: 'true でインタラクション扱いから外す (impression / slide_view 用)' },
          {
            label: '└ job_title / item_id / widget / カスタム',
            code: 'ウィジェット固有のカスタムパラメーター — GTM/GA4 でカスタムディメンション設定が必要',
          },
        ],
        sample: `<button onclick="trillTrack('タップ', {
  event_category: 'coupon_widget',
  event_label: 'redeem_button',
  link_url: 'https://example.com/coupon',
  widget: 'summer-2026-promo'
})">
  クーポンをもらう
</button>`,
        note:
          '【既存サポート】 action = タップ / 完読 / page_view ; params = event_category, event_label, link_url, non_interaction は GTM 側で配線済み。' +
          '【追加が必要】 上記以外のカスタム action / params (例: widget, job_title, item_id) を計測ダッシュボードに反映するには、GTM のトリガー & 変数, ' +
          'および GA4 のカスタムディメンション / メトリクスを追加で設定する必要があります。' +
          '【注意】 params が空 ({}) や未指定だと dataLayer push されません。' +
          '末尾に ; や別の文を追加するとエラーになります。listener は event_category を自動補完しないので必要なら必ず含めてください。',
      },
      {
        title: '🚪 同タブ遷移 (trillNavigate)',
        body:
          'クリックで親ページを「同じタブ」で遷移させたい場合は trillNavigate(url) を使用します。' +
          'iframe sandbox に allow-top-navigation* は付与せず、postMessage 経由で親に URL を渡して、' +
          '親側で URL allow-list 検証を通過した URL のみが window.location.href にセットされます。',
        items: [
          {
            label: 'url (必須・quoted string)',
            code:
              '遷移先 URL。絶対 URL のみ可: https://example.com/path または //example.com/path (scheme-relative)。相対 URL (/article/123, ?q=1, #frag, htt, ./foo など) は曖昧さ回避のため拒否',
          },
          {
            label: 'params (オプション・shallow scalar object)',
            code: '指定すると trillTrack タップ イベントも自動発火し link_url が自動セット',
          },
          { label: '許可プロトコル', code: 'http:, https: のみ' },
          {
            label: '拒否プロトコル',
            code: `${JS_SCHEME}, data:, ${VB_SCHEME}, file:, blob: は無条件で reject`,
          },
          { label: '拒否ホスト', code: 'localhost, 127.0.0.1, 0.0.0.0, ::1 (loopback アドレス)' },
          { label: 'レート制限', code: 'ページロードあたり 1 回 (spam 防止)' },
        ],
        sample: `<!-- 同タブ遷移のみ -->
<button onclick="trillNavigate('https://example.com/landing')">
  詳しく見る
</button>

<!-- 同タブ遷移 + GA4 計測 (一発で完結) -->
<button onclick="trillNavigate('https://example.com/landing', {
  event_category: 'cta',
  event_label: 'newsletter_signup'
})">
  メルマガ登録
</button>

<!-- ネイティブリンク + JS ハンドラ (middle-click で新規タブにも対応) -->
<a href="https://example.com/article/123"
   onclick="trillNavigate('https://example.com/article/123'); return false;">
  記事を読む
</a>`,
        note:
          'allow-top-navigation* トークンは sandbox に含まれません — ウィジェット JS から直接親 URL を変更することは不可能。' +
          'すべての遷移は親ランタイムによって URL 検証されます。',
      },
      {
        title: '🎨 CSS スタイル',
        body:
          'インライン style="..." と <style> ブロックの両方に対応。' +
          `禁止パターン: expression(...), ${JS_SCHEME}, ${VB_SCHEME}, @import, </style> (style ブロック脱出防止)`,
      },
      {
        title: '📐 サイズ指定',
        items: [
          {
            label: '自動 (推奨)',
            code: '幅・高さ空欄 (= 0) → エディター上 100%、公開時は postMessage でコンテンツ高さに自動調整',
          },
          {
            label: '固定比率',
            code: '数値指定 → 縦横比固定、コンテナ幅に比例縮小',
          },
          {
            label: '範囲',
            code: '50 ≤ 幅 ≤ 1200px、50 ≤ 高さ ≤ 2000px。0 は「自動」のセンチネル。負数・非数値・範囲外はエラー',
          },
          {
            label: 'ランタイム上限',
            code:
              '公開サイトでの postMessage リサイズは最大 4000px (auto モードでウィジェットコンテンツが伸びても上限)',
          },
        ],
      },
      {
        title: '🔒 サンドボックス',
        items: [
          { label: '既定', code: 'allow-scripts (iframe 内でのみ動作)' },
          { label: '追加可能', code: 'allow-popups, allow-forms, allow-modals' },
          { label: '不可', code: 'allow-same-origin (サンドボックス回避につながるため自動削除)' },
        ],
      },
      {
        title: '💡 サンプル',
        body:
          '右上の「サンプルを挿入」ボタンで、許可されているタグ・スタイル・trillTrack 呼び出しを含む雛形を読み込めます。',
      },
      {
        title: '🤖 AI (Claude / ChatGPT) で HTML を生成する場合',
        body:
          'AI で HTML を生成する際は、以下の制約を必ずプロンプトに含めてください。これらを伝えないと、AI は禁止タグ (script, iframe, form など) や外部 JS、危険な onclick を含む HTML を生成しがちです。',
        items: [
          {
            label: '使えるタグのみ',
            code: 'div, span, section, p, h1〜h6, ul/ol/li, a, button, img, style, svg, table 系のみ',
          },
          { label: '禁止', code: 'script, iframe, object, embed, form, input, link, meta タグは使わない' },
          { label: 'スクリプト不可', code: '<script> や外部 JS なし。on* 属性は trillTrack(...) のみ' },
          { label: 'URL', code: 'href / src は http / https のみ (画像は data:image/... も可)' },
          { label: 'CSS', code: 'inline style か <style> ブロック。@import / expression() / javascript: 不可' },
          { label: 'サンドボックス', code: 'allow-same-origin は使わない (自動で削除されます)' },
          { label: 'サイズ', code: '幅・高さは省略可 (自動 100%) / または具体的な数値 (例 800×600)' },
        ],
        sample: `# プロンプト例 (Claude / ChatGPT に送る指示)

TRILL の HTML 埋め込み (trill-html-iframe-embed) 用の HTML を生成してください。

【表示したい内容】
カード型のクーポンウィジェット (商品画像・タイトル・「もらう」ボタン)。

【厳格な制約】
1. 使えるタグ: div, span, p, h1〜h6, ul/ol/li, a, button, img, style, svg, table 系のみ
2. 禁止タグ: script, iframe, object, embed, form, input, textarea, select, link, meta
3. <script> / 外部 JS は禁止。on* 属性は <button onclick="trillTrack('タップ', { event_category: 'x', event_label: 'y' })">
   の形のみ可 (末尾に他の文を付けないこと)
4. href / src は http / https のみ。data:text/html / javascript: / vbscript: は禁止
5. CSS は inline style または <style> ブロック内。@import / expression() / javascript: は禁止
6. サンドボックスを破る allow-same-origin は使わない
7. CSS の </style> 文字列は禁止 (style ブロック脱出防止)
8. クリック計測は trillTrack(action, params) を呼ぶこと
   - サポート済み action: タップ / 完読 / page_view
   - サポート済み params: event_category, event_label, link_url, non_interaction
   - params は必須・非空 (空の {} だと dataLayer に送られません)
   - 上記以外の action / params を使う場合は GTM/GA4 の追加設定が必要
9. 同タブ遷移は trillNavigate(url, params?) を呼ぶこと
   - url は quoted-string、絶対 URL のみ (https://... または //host/...)
   - 相対 URL (/article/123, ?q=1, #frag, htt など) は曖昧さ回避のため拒否
   - params を渡すと trillTrack タップ + link_url 自動補完
   - javascript:, data:, file:, blob:, vbscript:, localhost / 127.0.0.1 は拒否
   - 1 ページロードあたり 1 回のみ navigate 可能 (spam 防止)`,
        note:
          '生成された HTML は、このダイアログのプレビューで検証されます。エラーが出たら該当箇所を AI に伝えて修正してもらってください。',
      },
      {
        title: '🩹 よくあるエラーと対処',
        items: [
          {
            label: '<script> タグは使えません',
            code:
              'AI が「インタラクション用に script を入れる」と提案した場合 → 削除し、必要なら <button onclick="trillTrack(...)"> に置き換え',
          },
          {
            label: 'on* 属性は trillTrack のみ',
            code:
              "onclick=\"alert('x')\" など → onclick=\"trillTrack('タップ', { event_category: 'x', event_label: 'y' })\" " +
              'に書き換え (action/params はサポート済みのものを使う)',
          },
          {
            label: 'href="javascript:..." 禁止',
            code: '<a href="javascript:..."> → 削除するか、<button onclick="trillTrack(...)"> に置き換え',
          },
          {
            label: '<iframe> タグは入れ子不可',
            code: 'YouTube などを埋め込みたい場合は SNS 埋め込み機能 (別ボタン) を使用',
          },
          {
            label: '<form> 不可',
            code: 'フォーム送信が必要な場合は外部リンク (<a href="...">) で誘導してください',
          },
          {
            label: 'CSS @import 不可',
            code: '外部 CSS は読み込めません。必要なスタイルは <style> ブロックに展開',
          },
        ],
      },
    ],
  },

  en: {
    summary: 'Usage Guide (HTML Embed rules & allow-list)',
    overview:
      'Render arbitrary HTML/CSS inside a sandboxed <iframe>. Isolated from the parent page, ' +
      'content is validated and sanitized against the allow-list.',
    sections: [
      {
        title: '🛡️ Guardrails (3-layer defense)',
        body:
          'Submitted HTML/CSS goes through 3 layers of validation and isolation — even if one ' +
          'layer is bypassed, the others stop the threat (defense in depth). Pipeline: ' +
          'Editor → save-time validator (npm + Rails) → DB → public site renders inside ' +
          '<iframe srcdoc> with CSP and bridge script auto-injected.',
        items: [
          {
            label: '① Validation layer',
            code:
              'Rejects allow-list violations (tags / attributes / URL schemes / CSS patterns / ' +
              'dimensions / trillTrack & trillNavigate grammar) as error (save blocked) ' +
              'or warning (silently stripped). The same allow-list runs in npm (dialog preview) ' +
              'AND Rails (server-side) — both paths agree.',
          },
          {
            label: '② Sandbox layer',
            code:
              'iframe sandbox accepts only safe tokens. allow-scripts is required (cannot be removed). ' +
              'allow-same-origin, allow-top-navigation, allow-popups-to-escape-sandbox, ' +
              'allow-pointer-lock, allow-presentation are all auto-stripped.',
          },
          {
            label: '③ Content-Security-Policy (CSP) layer',
            code:
              "Auto-injected into srcdoc <head> (widget cannot override): default-src 'none'; " +
              "img-src https: data:; style-src 'unsafe-inline'; script-src 'unsafe-inline'; " +
              "font-src https: data:; connect-src 'none'",
          },
          {
            label: '✅ What runs',
            code:
              'inline <style>, inline style="...", inline <script> (bridge only), ' +
              'images via https: + data: URLs, web fonts via https: + data: URLs',
          },
          {
            label: '❌ What is blocked',
            code:
              'External JS / CSS / iframe, fetch / XHR / WebSocket from the widget ' +
              '(connect-src \'none\'), <form action="..."> submission to external servers',
          },
          {
            label: 'Error vs Warning',
            code:
              'Error → save is blocked (disallowed tag, denied URL scheme, denied CSS pattern, ' +
              'out-of-range dimension, invalid trillTrack/trillNavigate). ' +
              'Warning → silently stripped, save still succeeds (e.g. attribute not in allow-list).',
          },
          {
            label: 'Default values',
            code:
              'sandbox: allow-scripts. Dimensions: 50 ≤ width ≤ 1200, 50 ≤ height ≤ 2000 (0 = auto). ' +
              'postMessage resize cap: 4000px. Preview debounce: 300ms. ' +
              'trillNavigate: http(s) only, loopback hosts denied, 1 navigation per page load.',
          },
        ],
        note:
          'What you see in the dialog preview = what is saved = what is rendered in production ' +
          '(always identical). Anything that violates the rules is surfaced as an error before save, ' +
          'so dangerous code never reaches production. If you need behavior beyond the current ' +
          'rules, contact the platform team — extensions go through allow-list config, ' +
          'not validator bypasses.',
      },
      {
        title: '✅ Allowed tags',
        items: [
          { label: 'Layout', code: 'div, span, section, article, header, footer, main, nav, aside' },
          { label: 'Paragraphs & headings', code: 'p, h1–h6, blockquote, pre, code' },
          { label: 'Text formatting', code: 'em, strong, b, i, u, small, mark, sub, sup' },
          { label: 'Lists', code: 'ul, ol, li, dl, dt, dd' },
          { label: 'Images & media', code: 'img, picture, source, figure, figcaption' },
          { label: 'Links & buttons', code: 'a, button (onclick must be trillTrack(...) only)' },
          { label: 'Styles', code: '<style> blocks / inline style="..."' },
          { label: 'Tables', code: 'table, thead, tbody, tfoot, tr, th, td' },
          { label: 'SVG', code: 'svg, path, g, rect, circle, line, polyline, polygon, text' },
          { label: 'Breaks & rules', code: 'br, hr' },
        ],
      },
      {
        title: '🚫 Disallowed tags (auto-stripped)',
        body: 'script, iframe, object, embed, form, input, textarea, select, meta, base, link, frame, frameset, applet',
      },
      {
        title: '🔗 URL schemes',
        items: [
          { label: 'href', code: 'http, https, mailto, tel' },
          { label: 'src', code: 'http, https, data:image/...' },
          {
            label: 'Denied',
            code: `${JS_SCHEME}, ${VB_SCHEME}, data:text/html (whitespace-obfuscated variants are also caught)`,
          },
        ],
      },
      {
        title: '⚡ GA4 Event handler (trillTrack / trillNavigate)',
        body:
          'on* attributes (onclick, etc.) are allowed on <a> / <button> only as a single ' +
          'trillTrack(action, params) OR trillNavigate(url, params?) call. Messages posted from ' +
          'inside the iframe are forwarded to the parent page’s GA4 (gtag) and GTM dataLayer. ' +
          'Multiple statements (`trillTrack(...); other()`) or other function calls are rejected.',
        items: [
          {
            label: 'action (required, string)',
            code:
              'GA4 event name. Currently supported by the tracking pipeline: タップ (tap), 完読 (read-through), page_view',
          },
          {
            label: 'params (REQUIRED, non-empty object)',
            code: 'GA4 event parameters object — forwarded verbatim to gtag. Missing / empty ({}) drops the event.',
          },
          {
            label: '└ event_category (recommended)',
            code:
              'Grouping label. Examples in trill-rails: 元記事を読む / 続きを読む / 自社プロモーション_banner_click',
          },
          { label: '└ event_label', code: 'Human-readable identifier of the target' },
          { label: '└ link_url', code: 'Destination URL (recommended for tap events)' },
          { label: '└ value', code: 'Numeric value (GA4 convention)' },
          { label: '└ non_interaction', code: 'true to flag the event as non-interaction (impression / slide_view)' },
          {
            label: '└ job_title / item_id / widget / custom',
            code: 'Widget-specific custom params — require additional GTM / GA4 custom dimension config',
          },
        ],
        sample: `<button onclick="trillTrack('タップ', {
  event_category: 'coupon_widget',
  event_label: 'redeem_button',
  link_url: 'https://example.com/coupon',
  widget: 'summer-2026-promo'
})">
  Redeem coupon
</button>`,
        note:
          'Already wired: action = タップ / 完読 / page_view ; params = event_category, event_label, link_url, non_interaction are already plumbed through GTM. ' +
          'Needs more config: any custom action / param (e.g. widget, job_title, item_id) requires extra GTM triggers + variables and GA4 custom dimensions/metrics to surface in dashboards. ' +
          'Note: empty / missing params drops the event from the dataLayer. Trailing ; or extra statements after the call are rejected. The listener does not auto-inject event_category, so include it explicitly when needed.',
      },
      {
        title: '🚪 Same-tab navigation (trillNavigate)',
        body:
          'To navigate the parent page in the SAME tab from inside a widget, call trillNavigate(url). ' +
          'The iframe sandbox does NOT include allow-top-navigation* — instead the widget posts a ' +
          'message to the parent which validates the URL against an allow-list before setting ' +
          'window.location.href.',
        items: [
          {
            label: 'url (required, quoted string)',
            code:
              'Absolute URL only: https://example.com/path or //example.com/path (scheme-relative). Relative URLs (/article/123, ?q=1, #frag, htt, ./foo, etc.) are rejected to avoid ambiguity — spell out the full URL.',
          },
          {
            label: 'params (optional, shallow scalar object)',
            code: 'When provided, also emits a trillTrack タップ event with link_url auto-filled',
          },
          { label: 'Allowed protocols', code: 'http:, https: only' },
          {
            label: 'Denied protocols',
            code: `${JS_SCHEME}, data:, ${VB_SCHEME}, file:, blob: — rejected unconditionally`,
          },
          { label: 'Denied hosts', code: 'localhost, 127.0.0.1, 0.0.0.0, ::1 (loopback addresses)' },
          { label: 'Rate limit', code: 'At most 1 navigation per page load (anti-spam)' },
        ],
        sample: `<!-- Navigate only -->
<button onclick="trillNavigate('https://example.com/landing')">
  Learn more
</button>

<!-- Navigate + GA4 tracking in a single call -->
<button onclick="trillNavigate('https://example.com/landing', {
  event_category: 'cta',
  event_label: 'newsletter_signup'
})">
  Sign up
</button>

<!-- Native link + JS handler (middle-click still opens in new tab) -->
<a href="https://example.com/article/123"
   onclick="trillNavigate('https://example.com/article/123'); return false;">
  Read article
</a>`,
        note:
          'No allow-top-navigation* token is granted to the sandbox — widget JS cannot directly ' +
          'change the parent URL. Every navigation is URL-validated by the parent runtime.',
      },
      {
        title: '🎨 CSS',
        body:
          'Both inline style="..." and <style> blocks are supported. ' +
          `Denied patterns: expression(...), ${JS_SCHEME}, ${VB_SCHEME}, @import, </style> (prevents style raw-text breakout).`,
      },
      {
        title: '📐 Sizing',
        items: [
          {
            label: 'Auto (recommended)',
            code: 'Leave width/height blank (= 0) → 100% in editor; postMessage auto-fits height on the public site.',
          },
          {
            label: 'Fixed aspect ratio',
            code: 'Specify numbers → aspect ratio locked, scales proportionally to container width.',
          },
          {
            label: 'Range',
            code:
              '50 ≤ width ≤ 1200px, 50 ≤ height ≤ 2000px. 0 is the "auto" sentinel. Negatives / non-numeric / out-of-range raise an error.',
          },
          {
            label: 'Runtime cap',
            code:
              'postMessage resize on the public site is capped at 4000px (anti-runaway for auto-mode widgets with very tall content).',
          },
        ],
      },
      {
        title: '🔒 Sandbox',
        items: [
          { label: 'Default', code: 'allow-scripts (scripts only run inside the iframe)' },
          { label: 'Optional', code: 'allow-popups, allow-forms, allow-modals' },
          { label: 'Never', code: 'allow-same-origin (would escape the sandbox — auto-stripped)' },
        ],
      },
      {
        title: '💡 Sample',
        body:
          'Click "サンプルを挿入" (top-right) to load a starter template with allowed tags, styles, and a trillTrack call.',
      },
      {
        title: '🤖 Generating HTML with AI (Claude / ChatGPT)',
        body:
          'When asking an AI to generate the HTML, include the constraints below in your prompt. Without them, AIs will usually emit forbidden tags (script, iframe, form…), external JS, or unsafe onclick handlers that the validator will reject.',
        items: [
          {
            label: 'Allowed tags only',
            code: 'div, span, section, p, h1–h6, ul/ol/li, a, button, img, style, svg, table family',
          },
          { label: 'Forbidden', code: 'script, iframe, object, embed, form, input, link, meta — never include these' },
          {
            label: 'No scripts',
            code: 'No <script> or external JS. on* attrs must be a single trillTrack(...) call only',
          },
          { label: 'URLs', code: 'href / src must be http or https (image src may also be data:image/...)' },
          { label: 'CSS', code: 'inline style or <style> blocks. No @import / expression() / javascript:' },
          { label: 'Sandbox', code: 'Never use allow-same-origin (stripped automatically)' },
          { label: 'Sizing', code: 'Omit width/height (auto 100%) or specify both (e.g. 800×600)' },
        ],
        sample: `# Prompt template (send this to Claude / ChatGPT)

Generate HTML for a TRILL HTML embed (trill-html-iframe-embed).

[What to display]
A coupon card widget (product image, title, and a "Get it" button).

[Strict constraints]
1. Allowed tags only: div, span, p, h1–h6, ul/ol/li, a, button, img, style, svg, table family
2. Forbidden tags: script, iframe, object, embed, form, input, textarea, select, link, meta
3. No <script> or external JS. on* attributes must be a single
   <button onclick="trillTrack('タップ', { event_category: 'x', event_label: 'y' })">
   call (no trailing statements)
4. href / src: http or https only. Never data:text/html / javascript: / vbscript:
5. CSS: inline style or <style> block. No @import / expression() / javascript:
6. Do not use sandbox bypass tokens like allow-same-origin
7. The string </style> is forbidden in CSS (prevents style raw-text breakout)
8. Track clicks via trillTrack(action, params):
   - Supported actions: タップ / 完読 / page_view
   - Supported params: event_category, event_label, link_url, non_interaction
   - params is REQUIRED and non-empty ({} drops the event)
   - Any other custom action / param needs additional GTM/GA4 config
9. Same-tab navigation: call trillNavigate(url, params?)
   - url must be a quoted-string ABSOLUTE URL (https://... or //host/...)
   - Relative URLs (/article/123, ?q=1, #frag, htt, etc.) are rejected to
     avoid ambiguity — always spell out the full URL
   - When params are provided, a trillTrack タップ event also fires with link_url auto-filled
   - javascript:, data:, file:, blob:, vbscript:, localhost / 127.0.0.1 are rejected
   - At most 1 navigation per page load (anti-spam)`,
        note:
          'The generated HTML is validated by the preview in this dialog. If errors appear, paste them back to the AI and ask it to fix the offending parts.',
      },
      {
        title: '🩹 Common errors & fixes',
        items: [
          {
            label: '<script> tags are not allowed',
            code:
              'If the AI inserts a <script> for interactivity → remove it; replace with <button onclick="trillTrack(...)"> if a click handler is needed',
          },
          {
            label: 'on* must be trillTrack only',
            code:
              'onclick="alert(\'x\')" → ' +
              "onclick=\"trillTrack('タップ', { event_category: 'x', event_label: 'y' })\" " +
              '(use a supported action / params)',
          },
          {
            label: 'No javascript: URLs',
            code: '<a href="javascript:..."> → remove, or replace with <button onclick="trillTrack(...)">',
          },
          {
            label: 'Nested <iframe> not allowed',
            code: 'For YouTube etc., use the dedicated SNS embed button (separate from this dialog)',
          },
          {
            label: '<form> not allowed',
            code: 'For form-like flows, link out (<a href="...">) to an external page',
          },
          {
            label: 'No CSS @import',
            code: 'External CSS cannot be loaded — inline the required rules into the <style> block',
          },
        ],
      },
    ],
  },
}

const containerStyle = {
  fontSize: 13,
  lineHeight: 1.7,
  padding: 12,
  background: '#fafafa',
  borderRadius: 4,
  border: '1px solid #e0e0e0',
}

const sectionStyle = { marginTop: 12 }
const headingStyle = { fontSize: 13, fontWeight: 'bold', margin: '8px 0 4px' }
const codeStyle = {
  fontFamily: 'monospace',
  background: '#f0f0f0',
  padding: '1px 4px',
  borderRadius: 3,
  fontSize: 12,
}
const preStyle = {
  fontFamily: 'monospace',
  background: '#f8f9fa',
  padding: 10,
  borderRadius: 4,
  fontSize: 12,
  overflow: 'auto',
  margin: '4px 0',
}
const noteStyle = { margin: '4px 0', color: '#888' }

export function HtmlEmbedGuide(props) {
  const lang = SUPPORTED_LANGS.includes(props.lang) ? props.lang : DEFAULT_LANG
  const guide = GUIDES[lang]

  return (
    <div style={containerStyle}>
      <p style={{ marginTop: 0 }}>{guide.overview}</p>

      {guide.sections.map(section => (
        <section key={section.title} style={sectionStyle}>
          <h4 style={headingStyle}>{section.title}</h4>

          {section.body && <p style={{ margin: '4px 0' }}>{section.body}</p>}

          {section.items && (
            <ul style={{ margin: '4px 0 4px 20px', padding: 0 }}>
              {section.items.map(item => (
                <li key={item.label}>
                  <strong>{item.label}</strong>: <code style={codeStyle}>{item.code}</code>
                </li>
              ))}
            </ul>
          )}

          {section.sample && <pre style={preStyle}>{section.sample}</pre>}

          {section.note && <p style={noteStyle}>※ {section.note}</p>}
        </section>
      ))}
    </div>
  )
}

HtmlEmbedGuide.propTypes = {
  lang: PropTypes.oneOf(SUPPORTED_LANGS),
}

HtmlEmbedGuide.defaultProps = {
  lang: DEFAULT_LANG,
}

export function getGuideSummary(lang) {
  const safeLang = SUPPORTED_LANGS.includes(lang) ? lang : DEFAULT_LANG
  return GUIDES[safeLang].summary
}
